import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Loading } from "../components/Loader";
import { apiErrorMessage } from "../shared/apiErrors";
import { createPortal } from "react-dom";
import { useDispatch, useSelector } from "react-redux";
import { Link, useLocation, useParams } from "react-router-dom";
import Barcode from "react-barcode";
import { apiFetch, getApiBaseUrl } from "../shared/api";
import { getAccessToken } from "../shared/tokenStorage";
import {
  fetchStockTransferDetail,
  selectStockTransferById,
  selectStockTransferErrorById,
  selectStockTransferStatusById,
} from "../features/stockTransfers/stockTransfersSlice";
import {
  fetchProductDetail,
  selectProductById,
  fetchProducts,
  fetchCategorySuggestions,
  selectProducts,
  selectProductsStatus,
  selectCategorySuggestions,
} from "../features/products/productsSlice";
import "./StockTransferDetailPage.css";

async function downloadFile(url, filename) {
  const token = getAccessToken();
  const res = await fetch(`${getApiBaseUrl()}${url}`, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  if (!res.ok) throw new Error("Ошибка экспорта");
  const blob = await res.blob();
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob);
  a.download = filename;
  a.click();
  URL.revokeObjectURL(a.href);
}

// ─── Print label constants (50×40mm) ───────────────────────────────────────
const LABEL_W_MM = 38;
const LABEL_H_MM = 30;

const labelPrintCss = `
  @page { size: ${LABEL_W_MM}mm ${LABEL_H_MM}mm portrait; margin: 0; }
  @media print {
    html, body { margin: 0 !important; padding: 0 !important; }
    body > *:not(#transfer-print-root) { display: none !important; }
    #transfer-print-root {
      display: block !important;
      position: static !important;
      margin: 0 !important;
      padding: 0 !important;
      width: ${LABEL_W_MM}mm !important;
      height: auto !important;
      overflow: visible !important;
    }
    .transfer-print-label {
      display: block !important;
      position: relative !important;
      margin: 0 !important;
      padding: 0 !important;
      width: ${LABEL_W_MM}mm !important;
      height: ${LABEL_H_MM}mm !important;
      overflow: hidden !important;
      box-sizing: border-box !important;
      page-break-before: always !important;
      break-before: page !important;
    }
    .transfer-print-label:first-child {
      page-break-before: auto !important;
      break-before: auto !important;
    }
  }
`;

// Absolute positioning для 40×30мм
// AUF:      top 0mm,  h 4mm  → конец 4mm
// Название: top 4mm,  h 5mm  → конец 9mm
// Штрихкод: top 9mm,  h 13mm → конец 22mm
// Цена:     top 22mm, h 7mm  → конец 29mm < 30mm ✓
const labelWrapStyle = {
  position: "relative",
  width: `${LABEL_W_MM}mm`, height: `${LABEL_H_MM}mm`,
  boxSizing: "border-box",
  background: "#fff", color: "#000",
  fontFamily: "Arial, sans-serif", overflow: "hidden",
};
const labelCompanyStyle = {
  position: "absolute", top: "1mm", left: "1mm", right: "1mm", height: "4mm",
  overflow: "hidden",
  fontSize: 14, fontWeight: 900, letterSpacing: 0.5,
  textAlign: "center",
  display: "flex", alignItems: "center", justifyContent: "center",
};
const labelNameStyle = {
  position: "absolute", top: "5mm", left: "1mm", right: "1mm", height: "8mm",
  overflow: "hidden",
  fontSize: 11, fontWeight: 700, lineHeight: 1.1,
  textAlign: "center", wordBreak: "break-word",
  display: "flex", alignItems: "center", justifyContent: "center",
};
const labelBarcodeStyle = {
  position: "absolute", top: "13mm", left: "1mm", right: "1mm", height: "12mm",
  overflow: "hidden",
  display: "flex", justifyContent: "left", alignItems: "flex-start",
};
const labelPriceStyle = {
  position: "absolute", top: "24mm", left: "1mm", right: "1mm", height: "7mm",
  overflow: "hidden",
  fontSize: 14, fontWeight: 900, textAlign: "center",
  display: "flex", alignItems: "center", justifyContent: "center",
};
// ───────────────────────────────────────────────────────────────────────────

function formatDateTime(value) {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleString("ru-RU");
}

function formatMoney(value) {
  const num = Number(value || 0);
  if (Number.isNaN(num)) return "0.00";
  return num.toLocaleString("ru-RU", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function formatUser(value) {
  if (!value) return "—";
  if (typeof value === "number" || typeof value === "string") return `#${value}`;
  if (typeof value === "object") {
    const username = value.username ? `@${value.username}` : null;
    const fullName = [value.first_name, value.last_name].filter(Boolean).join(" ").trim() || null;
    const id = value.id != null ? `#${value.id}` : null;
    return [fullName, username, id].filter(Boolean).join(" ") || "—";
  }
  return "—";
}

function stockTransferStatusLabel(status) {
  const s = String(status || "").toLowerCase();
  if (s === "draft") return "Черновик";
  if (s === "submitted") return "Отправлено";
  if (s === "received") return "Получено";
  if (s === "cancelled") return "Отменено";
  return status || "—";
}

export default function StockTransferDetailPage() {
  const { transferId } = useParams();
  const dispatch = useDispatch();
  const location = useLocation();

  const fromList = useMemo(() => {
    const from = location.state?.from;
    return typeof from === "string" && from.startsWith("/") ? from : null;
  }, [location.state]);

  const transfer = useSelector(selectStockTransferById(transferId));
  const loadStatus = useSelector(selectStockTransferStatusById(transferId));
  const error = useSelector(selectStockTransferErrorById(transferId));

  const fetchTransfer = useCallback(() => {
    dispatch(fetchStockTransferDetail({ transferId }));
  }, [dispatch, transferId]);

  useEffect(() => {
    fetchTransfer();
  }, [fetchTransfer]);

  const items = useMemo(() => (Array.isArray(transfer?.items) ? transfer.items : []), [transfer?.items]);
  const itemsSorted = useMemo(() => {
    const list = Array.isArray(items) ? [...items] : [];
    // Show newest items first (higher id usually means newer)
    return list.sort((a, b) => Number(b?.id || 0) - Number(a?.id || 0));
  }, [items]);
  const statusNorm = String(transfer?.status || "").toLowerCase();
  const isDraft = statusNorm === "draft";
  const isSubmitted = statusNorm === "submitted";

  // --- Остаток на складе-источнике ---
  // Приходит с сервера в самой позиции (`stock_at_from_store`). Раньше остатки тянулись
  // отдельным пагинированным запросом и склеивались здесь по product_id, но ответ
  // обрезался лимитом страницы, и товары за его пределами показывали «—».

  // ─── Print price tags ─────────────────────────────────────────────────────
  const [printCheckedById, setPrintCheckedById] = useState({});
  const [printNowLabels, setPrintNowLabels] = useState(null);

  // Подгружаем данные о продуктах (для цены) при изменении списка товаров
  useEffect(() => {
    const ids = [...new Set((items || []).map((it) => it?.product).filter(Boolean))];
    ids.forEach((pid) => dispatch(fetchProductDetail({ productId: pid })));
  }, [dispatch, items]);

  const productById = useSelector((state) =>
    Object.fromEntries(
      [...new Set((items || []).map((it) => it?.product).filter(Boolean))].map((pid) => [
        String(pid),
        selectProductById(pid)(state),
      ])
    )
  );

  const togglePrintCheck = useCallback((itemId, checked) => {
    setPrintCheckedById((prev) => ({ ...prev, [String(itemId)]: !!checked }));
  }, []);

  const checkedCount = Object.values(printCheckedById).filter(Boolean).length;

  const onPrintChecked = useCallback(() => {
    const checkedItems = (items || []).filter((it) => printCheckedById[String(it.id)]);
    if (checkedItems.length === 0) return;

    const labels = [];
    for (const it of checkedItems) {
      const qty = Math.max(1, Number(it.quantity) || 1);
      const barcode = String(it?.product_barcode ?? it?.product?.barcode ?? "").trim();
      const name = String(it?.product_name ?? it?.product?.name ?? "").trim() || "—";
      const prod = it?.product != null ? productById[String(it.product)] : null;
      const priceRaw = it?.retail_price ?? it?.cost_price ?? prod?.cost_price ?? prod?.retail_price;
      const price = priceRaw != null && Number.isFinite(Number(priceRaw)) ? Number(priceRaw) : null;
      for (let i = 0; i < qty; i++) labels.push({ barcode, name, price });
    }
    if (labels.length === 0) return;
    setPrintNowLabels(labels);
  }, [items, printCheckedById, productById]);

  // Когда Portal отрендерен — печатаем
  useEffect(() => {
    if (printNowLabels === null) return;
    const t = setTimeout(() => {
      window.print();
      setPrintNowLabels(null);
      setPrintCheckedById({});
    }, 100);
    return () => clearTimeout(t);
  }, [printNowLabels]);
  // ─────────────────────────────────────────────────────────────────────────

  // --- Export (Excel via backend, PDF via browser print — see handlePrintTransferPdf) ---
  const [exportLoading, setExportLoading] = useState(null); // "excel" | null
  const [exportError, setExportError] = useState(null);

  const onExportExcel = useCallback(async () => {
    setExportLoading("excel");
    setExportError(null);
    try {
      await downloadFile(
        `/api/product/stock-transfers/${transferId}/export-excel/`,
        `stock-transfer-${transferId}.xlsx`
      );
    } catch (err) {
      setExportError(err?.message || "Ошибка экспорта");
    } finally {
      setExportLoading(null);
    }
  }, [transferId]);

  const handlePrintTransferPdf = useCallback(() => {
    if (!transfer) return;
    const rows = itemsSorted;
    const itemRows = rows
      .map(
        (it, idx) => `
      <tr class="${idx % 2 === 0 ? "row-even" : "row-odd"}">
        <td class="td-num">${idx + 1}</td>
        <td class="td-name">${it.product_name || "—"}</td>
        <td class="td-code">${it.product_barcode || "—"}</td>
        <td class="td-right">${it.quantity ?? "—"}</td>
        <td class="td-right">${formatMoney(it.wholesale_price)}</td>
        <td class="td-right td-total">${formatMoney(it.line_wholesale_total)}</td>
      </tr>`
      )
      .join("");

    const statusLabels = {
      draft: "Черновик",
      submitted: "Отправлено",
      received: "Получено",
      cancelled: "Отменено",
    };

    const html = `<!DOCTYPE html>
<html lang="ru">
<head>
<meta charset="UTF-8">
<title>Перемещение #${transfer.id}</title>
<style>
  * { box-sizing: border-box; margin: 0; padding: 0; }
  body { font-family: 'Segoe UI', Arial, sans-serif; font-size: 11px; color: #1a1a1a; background: #fff; padding: 20px 28px; }
  .doc-header { display: flex; justify-content: space-between; align-items: flex-start; border-bottom: 2px solid #2563eb; padding-bottom: 12px; margin-bottom: 16px; }
  .doc-header .left .company { font-size: 18px; font-weight: 700; color: #1e40af; letter-spacing: 0.5px; }
  .doc-header .left .subtitle { font-size: 11px; color: #64748b; margin-top: 2px; }
  .doc-header .right { text-align: right; }
  .doc-header .right .doc-title { font-size: 20px; font-weight: 700; color: #1e40af; }
  .doc-header .right .doc-num { font-size: 13px; color: #64748b; margin-top: 2px; }
  .info-section { display: grid; grid-template-columns: 1fr 1fr; gap: 0; border: 1px solid #e2e8f0; border-radius: 4px; margin-bottom: 18px; overflow: hidden; }
  .info-block { padding: 10px 14px; background: #f8fafc; }
  .info-block:nth-child(even) { background: #fff; border-left: 1px solid #e2e8f0; }
  .info-row { display: flex; align-items: baseline; gap: 8px; margin-bottom: 5px; }
  .info-row:last-child { margin-bottom: 0; }
  .info-label { font-size: 10px; color: #64748b; min-width: 110px; text-transform: uppercase; letter-spacing: 0.4px; }
  .info-value { font-size: 11.5px; font-weight: 600; color: #1e293b; }
  .badge { display: inline-block; padding: 1px 8px; border-radius: 10px; font-size: 10px; font-weight: 600; }
  .badge-draft { background: #f1f5f9; color: #64748b; border: 1px solid #cbd5e1; }
  .badge-submitted { background: #e0f2fe; color: #075985; border: 1px solid #7dd3fc; }
  .badge-received { background: #dcfce7; color: #166534; border: 1px solid #86efac; }
  .badge-cancelled { background: #fee2e2; color: #991b1b; border: 1px solid #fca5a5; }
  .section-title { font-size: 11px; font-weight: 700; color: #1e40af; text-transform: uppercase; letter-spacing: 0.8px; margin-bottom: 6px; margin-top: 18px; padding-bottom: 4px; border-bottom: 1px solid #bfdbfe; }
  table { width: 100%; border-collapse: collapse; font-size: 10.5px; }
  th { background: #1e40af; color: #fff; padding: 6px 8px; text-align: left; font-size: 10px; font-weight: 600; text-transform: uppercase; letter-spacing: 0.4px; white-space: nowrap; }
  td { padding: 5px 8px; border-bottom: 1px solid #e2e8f0; vertical-align: middle; }
  .row-even { background: #fff; }
  .row-odd { background: #f8fafc; }
  .td-right { text-align: right; white-space: nowrap; }
  .td-num { text-align: center; color: #94a3b8; width: 28px; }
  .td-code { color: #64748b; font-family: monospace; font-size: 9.5px; }
  .td-name { font-weight: 500; }
  .td-total { font-weight: 700; color: #1e293b; }
  .totals-wrap { display: flex; justify-content: flex-end; margin-top: 10px; }
  .totals-table { min-width: 260px; }
  .totals-table td { border: none; padding: 3px 8px; }
  .totals-table .tot-label { color: #64748b; text-align: right; font-size: 10.5px; }
  .totals-table .tot-val { text-align: right; font-weight: 600; font-size: 11px; min-width: 90px; }
  .totals-table .grand-row td { border-top: 2px solid #1e40af; padding-top: 6px; font-size: 13px; font-weight: 700; color: #1e40af; }
  .doc-footer { margin-top: 28px; border-top: 1px solid #e2e8f0; padding-top: 8px; display: flex; justify-content: space-between; color: #94a3b8; font-size: 9.5px; }
  @media print {
    body { padding: 0; }
    @page { margin: 14mm 12mm; }
  }
</style>
</head>
<body>
  <div class="doc-header">
    <div class="left">
      <div class="company">АУФ</div>
      <div class="subtitle">Перемещение между складами</div>
    </div>
    <div class="right">
      <div class="doc-title">Накладная</div>
      <div class="doc-num">№ ${transfer.id} от ${formatDateTime(transfer.created_at)}</div>
    </div>
  </div>

  <div class="info-section">
    <div class="info-block">
      <div class="info-row"><span class="info-label">Откуда</span><span class="info-value">${transfer.from_store_name || "—"}</span></div>
      <div class="info-row"><span class="info-label">Куда</span><span class="info-value">${transfer.to_store_name || "—"}</span></div>
    </div>
    <div class="info-block">
      <div class="info-row"><span class="info-label">Статус</span><span class="info-value"><span class="badge badge-${transfer.status || "draft"}">${statusLabels[transfer.status] || transfer.status}</span></span></div>
      <div class="info-row"><span class="info-label">Отправлено</span><span class="info-value">${formatDateTime(transfer.submitted_at)}</span></div>
    </div>
  </div>

  <div class="section-title">Товары</div>
  <table>
    <thead>
      <tr>
        <th class="td-num">#</th>
        <th>Наименование</th>
        <th>Штрих-код</th>
        <th class="td-right">Кол-во</th>
        <th class="td-right">Опт. цена</th>
        <th class="td-right">Сумма</th>
      </tr>
    </thead>
    <tbody>${itemRows || `<tr><td colspan="6" style="text-align:center;color:#94a3b8;padding:10px">Нет позиций</td></tr>`}</tbody>
  </table>

  <div class="totals-wrap">
    <table class="totals-table">
      <tbody>
        <tr class="grand-row"><td class="tot-label">Итого по опт. цене</td><td class="tot-val">${formatMoney(transfer.wholesale_total)}</td></tr>
      </tbody>
    </table>
  </div>

  ${transfer.comment ? `<div class="notes-box" style="background:#fefce8;border:1px solid #fef08a;border-radius:4px;padding:8px 12px;font-size:10.5px;color:#713f12;margin-top:14px"><strong>Комментарий:</strong> ${transfer.comment}</div>` : ""}

  <div class="doc-footer">
    <span>Сформировано: ${new Date().toLocaleString("ru-RU")}</span>
    <span>АУФ — Перемещение #${transfer.id}</span>
  </div>

  <script>
    window.onload = function() {
      window.focus();
      window.print();
    };
  </script>
</body>
</html>`;

    const w = window.open("", "_blank", "width=900,height=700");
    if (!w) {
      window.alert("Разрешите всплывающие окна для печати PDF");
      return;
    }
    w.document.write(html);
    w.document.close();
  }, [transfer, itemsSorted]);

  // --- Barcode / название / категория -> add item to transfer (immediately) ---
  const [barcode, setBarcode] = useState("");
  const [scanStatus, setScanStatus] = useState("idle"); // idle | loading | saving | failed
  const [scanError, setScanError] = useState(null);
  const [searchDropdownOpen, setSearchDropdownOpen] = useState(false);
  const [selectedCategoryId, setSelectedCategoryId] = useState(null);

  const [deleteStatusById, setDeleteStatusById] = useState(() => ({})); // itemId -> saving/failed/idle
  const [deleteErrorById, setDeleteErrorById] = useState(() => ({})); // itemId -> message

  const [qtyDraftById, setQtyDraftById] = useState(() => ({})); // itemId -> string qty
  const [saveStatusById, setSaveStatusById] = useState(() => ({})); // itemId -> saving/failed/idle
  const [saveErrorById, setSaveErrorById] = useState(() => ({})); // itemId -> message
  const [editingItemId, setEditingItemId] = useState(null); // string | null
  const [submitStatus, setSubmitStatus] = useState("idle"); // idle | saving | failed
  const [submitError, setSubmitError] = useState(null);
  const [receiveStatus, setReceiveStatus] = useState("idle"); // idle | saving | failed
  const [receiveError, setReceiveError] = useState(null);
  const [cancelStatus, setCancelStatus] = useState("idle"); // idle | saving | failed
  const [cancelError, setCancelError] = useState(null);

  const barcodeNormalized = useMemo(() => String(barcode || "").replace(/\s+/g, "").trim(), [barcode]);
  const searchQueryTrimmed = useMemo(() => String(barcode || "").trim(), [barcode]);
  const isNumericQuery = barcodeNormalized.length > 0 && /^\d+$/.test(barcodeNormalized);
  const barcodeInputRef = useRef(null);
  const barcodeSearchTimeoutRef = useRef(null);
  const searchDebounceRef = useRef(null);
  const qtyInputRefByItemId = useRef({}); // itemId -> input element

  const searchResults = useSelector(selectProducts);
  const searchStatus = useSelector(selectProductsStatus);
  const categoryResults = useSelector(selectCategorySuggestions);

  // Autofocus поля сканирования при открытии черновика перемещения.
  useEffect(() => {
    if (isDraft && barcodeInputRef.current) {
      barcodeInputRef.current.focus();
    }
  }, [isDraft]);

  // Расширенный поиск: если введённое значение не похоже на штрихкод (не только цифры) —
  // ищем товары/категории по имени, вместо автосканирования по длине.
  useEffect(() => {
    if (!isDraft) return;
    if (isNumericQuery || !searchQueryTrimmed) {
      if (!selectedCategoryId) setSearchDropdownOpen(false);
      if (searchDebounceRef.current) {
        clearTimeout(searchDebounceRef.current);
        searchDebounceRef.current = null;
      }
      return;
    }
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(() => {
      searchDebounceRef.current = null;
      dispatch(fetchProducts({ search: searchQueryTrimmed, categoryId: selectedCategoryId, pageSize: 20 }));
      if (!selectedCategoryId) {
        dispatch(fetchCategorySuggestions({ search: searchQueryTrimmed, pageSize: 10 }));
      }
      setSearchDropdownOpen(true);
    }, 250);
    return () => {
      if (searchDebounceRef.current) {
        clearTimeout(searchDebounceRef.current);
        searchDebounceRef.current = null;
      }
    };
  }, [dispatch, isDraft, isNumericQuery, searchQueryTrimmed, selectedCategoryId]);

  const lookupProductInStore = useCallback(
    async (code) => {
      const c = String(code || "").replace(/\s+/g, "").trim();
      if (!c) throw new Error("barcode is required");
      const storeId = transfer?.from_store;
      if (!storeId) {
        const e = new Error("store_id is required");
        e.data = { error: "Не указан from_store у перемещения (store_id обязателен для поиска)" };
        throw e;
      }
      return await apiFetch(
        `/api/product/products/check-inventory-store-by-barcode/?barcode=${encodeURIComponent(c)}&store_id=${encodeURIComponent(
          String(storeId)
        )}`,
        { method: "GET" }
      );
    },
    [transfer?.from_store]
  );

  // Добавляет новую позицию, либо, если товар уже есть в списке, увеличивает её
  // количество на +1 через PATCH (непрерывное сканирование не должно останавливаться на ошибке).
  const addOrIncrementItem = useCallback(
    async ({ productId, existingItem }) => {
      if (existingItem?.id) {
        const newQty = Number(existingItem.quantity || 0) + 1;
        await apiFetch(`/api/product/stock-transfers/${transferId}/items/${existingItem.id}/`, {
          method: "PATCH",
          body: { quantity: newQty },
        });
      } else {
        await apiFetch(`/api/product/stock-transfers/${transferId}/items/`, {
          method: "POST",
          body: { product: productId, quantity: 1 },
        });
      }
    },
    [transferId]
  );

  const onScanAddByBarcode = useCallback(async () => {
    if (!isDraft) return;
    const c = barcodeNormalized;
    if (!c) return;

    setBarcode("");
    setSearchDropdownOpen(false);
    setScanStatus("loading");
    setScanError(null);
    try {
      const product = await lookupProductInStore(c);
      const existing = (items || []).find((x) => String(x?.product_barcode || "").trim() === c) || null;

      setScanStatus("saving");
      await addOrIncrementItem({ productId: product?.id, existingItem: existing });
      setScanStatus("idle");

      if (barcodeInputRef.current && typeof barcodeInputRef.current.focus === "function") {
        barcodeInputRef.current.focus();
      }
      fetchTransfer();
    } catch (err) {
      const msg = apiErrorMessage(err, "Не удалось добавить товар по штрихкоду");
      setScanStatus("failed");
      setScanError(msg);
      if (barcodeInputRef.current && typeof barcodeInputRef.current.focus === "function") {
        barcodeInputRef.current.focus();
      }
    }
  }, [addOrIncrementItem, barcodeInputRef, barcodeNormalized, fetchTransfer, isDraft, items, lookupProductInStore]);

  // Выбор товара из dropdown расширенного поиска (по имени/категории).
  const addProductFromSearch = useCallback(
    async (product) => {
      if (!isDraft || !product?.id) return;
      const existing = (items || []).find((x) => Number(x?.product) === Number(product.id)) || null;

      setBarcode("");
      setSearchDropdownOpen(false);
      setSelectedCategoryId(null);
      setScanStatus("saving");
      setScanError(null);
      try {
        await addOrIncrementItem({ productId: product.id, existingItem: existing });
        setScanStatus("idle");
        fetchTransfer();
      } catch (err) {
        const msg = apiErrorMessage(err, "Не удалось добавить товар");
        setScanStatus("failed");
        setScanError(msg);
      } finally {
        if (barcodeInputRef.current && typeof barcodeInputRef.current.focus === "function") {
          barcodeInputRef.current.focus();
        }
      }
    },
    [addOrIncrementItem, barcodeInputRef, fetchTransfer, isDraft, items]
  );

  // Автопоиск по штрихкоду: 12 символов — ждём 800 мс, 13 символов — запрос сразу
  useEffect(() => {
    if (!isDraft || !barcodeNormalized) {
      if (barcodeSearchTimeoutRef.current) {
        clearTimeout(barcodeSearchTimeoutRef.current);
        barcodeSearchTimeoutRef.current = null;
      }
      return;
    }
    const len = barcodeNormalized.length;
    if (len === 13) {
      if (barcodeSearchTimeoutRef.current) {
        clearTimeout(barcodeSearchTimeoutRef.current);
        barcodeSearchTimeoutRef.current = null;
      }
      onScanAddByBarcode();
      return;
    }
    if (len === 12) {
      if (barcodeSearchTimeoutRef.current) clearTimeout(barcodeSearchTimeoutRef.current);
      barcodeSearchTimeoutRef.current = setTimeout(() => {
        barcodeSearchTimeoutRef.current = null;
        onScanAddByBarcode();
      }, 800);
    } else {
      if (barcodeSearchTimeoutRef.current) {
        clearTimeout(barcodeSearchTimeoutRef.current);
        barcodeSearchTimeoutRef.current = null;
      }
    }
    return () => {
      if (barcodeSearchTimeoutRef.current) {
        clearTimeout(barcodeSearchTimeoutRef.current);
        barcodeSearchTimeoutRef.current = null;
      }
    };
  }, [barcodeNormalized, isDraft, onScanAddByBarcode]);

  const onDeleteItem = useCallback(
    async (itemId) => {
      if (!isDraft) return;
      const id = String(itemId);
      setDeleteStatusById((p) => ({ ...p, [id]: "saving" }));
      setDeleteErrorById((p) => ({ ...p, [id]: null }));
      try {
        await apiFetch(`/api/product/stock-transfers/${transferId}/items/${itemId}/`, { method: "DELETE" });
        setDeleteStatusById((p) => ({ ...p, [id]: "idle" }));
        fetchTransfer();
      } catch (err) {
        const msg = apiErrorMessage(err, "Не удалось удалить позицию");
        setDeleteStatusById((p) => ({ ...p, [id]: "failed" }));
        setDeleteErrorById((p) => ({ ...p, [id]: msg }));
      }
    },
    [fetchTransfer, isDraft, transferId]
  );

  const itemsKey = useMemo(() => (items || []).map((it) => `${it?.id}:${it?.quantity}`).join("|"), [items]);

  useEffect(() => {
    const next = {};
    for (const it of items || []) {
      next[String(it.id)] = it.quantity == null ? "" : String(it.quantity);
    }
    setQtyDraftById(next);
  }, [itemsKey, items]);

  const onSaveItemQty = useCallback(
    async (itemId) => {
      if (!isDraft) return;
      const id = String(itemId);
      const raw = String(qtyDraftById[id] ?? "").trim();
      const n = Number(raw);
      if (!Number.isFinite(n) || !Number.isInteger(n) || n <= 0) {
        setSaveStatusById((p) => ({ ...p, [id]: "failed" }));
        setSaveErrorById((p) => ({ ...p, [id]: "Количество должно быть целым числом > 0" }));
        return;
      }
      setSaveStatusById((p) => ({ ...p, [id]: "saving" }));
      setSaveErrorById((p) => ({ ...p, [id]: null }));
      try {
        await apiFetch(`/api/product/stock-transfers/${transferId}/items/${itemId}/`, {
          method: "PATCH",
          body: { quantity: n },
        });
        setSaveStatusById((p) => ({ ...p, [id]: "idle" }));
        setEditingItemId(null);
        fetchTransfer();
      } catch (err) {
        const msg = apiErrorMessage(err, "Не удалось сохранить количество");
        setSaveStatusById((p) => ({ ...p, [id]: "failed" }));
        setSaveErrorById((p) => ({ ...p, [id]: msg }));
      }
    },
    [fetchTransfer, isDraft, qtyDraftById, transferId]
  );

  const onSubmitTransfer = useCallback(async () => {
    if (!isDraft) return;
    setSubmitStatus("saving");
    setSubmitError(null);
    try {
      await apiFetch(`/api/product/stock-transfers/${transferId}/submit/`, { method: "POST" });
      setSubmitStatus("idle");
      fetchTransfer();
    } catch (err) {
      const msg = apiErrorMessage(err, "Не удалось отправить перемещение");
      setSubmitStatus("failed");
      setSubmitError(msg);
    }
  }, [fetchTransfer, isDraft, transferId]);

  const onReceiveTransfer = useCallback(async () => {
    if (!isSubmitted) return;
    setReceiveStatus("saving");
    setReceiveError(null);
    try {
      await apiFetch(`/api/product/stock-transfers/${transferId}/receive/`, { method: "POST" });
      setReceiveStatus("idle");
      fetchTransfer();
    } catch (err) {
      const msg = apiErrorMessage(err, "Не удалось отметить как получено");
      setReceiveStatus("failed");
      setReceiveError(msg);
    }
  }, [fetchTransfer, isSubmitted, transferId]);

  const onCancelTransfer = useCallback(async () => {
    if (statusNorm === "received" || statusNorm === "cancelled") return;
    setCancelStatus("saving");
    setCancelError(null);
    try {
      await apiFetch(`/api/product/stock-transfers/${transferId}/cancel/`, { method: "POST" });
      setCancelStatus("idle");
      fetchTransfer();
    } catch (err) {
      const msg = apiErrorMessage(err, "Не удалось отменить перемещение");
      setCancelStatus("failed");
      setCancelError(msg);
    }
  }, [fetchTransfer, statusNorm, transferId]);

  return (
    <div className="stock-transfer-detail-page">
      <div className="header">
        <div>
          <div className="breadcrumbs">
            <Link to={fromList || "/warehouse/transfers"} className="link">
              Перемещения по складам
            </Link>
            <span className="sep">/</span>
            <span className="muted">Перемещение #{transferId}</span>
          </div>
          <div className="title">Перемещение #{transferId}</div>
          <div className="sub-title">
            Детали перемещения и список позиций (items) из <span className="mono">/api/product/stock-transfers/{transferId}/</span>.
          </div>
        </div>

        <div className="header-actions">
          <Link to={`/warehouse/transfers/${transferId}/edit`} state={{ from: fromList }} className="btn-ghost-link">
            Редактировать
          </Link>
          {isDraft && (
            <button
              type="button"
              onClick={onSubmitTransfer}
              className="btn-primary"
              disabled={submitStatus === "saving"}
              title="Отправить перемещение"
            >
              {submitStatus === "saving" ? "Отправляю…" : "Отправить"}
            </button>
          )}
          {isSubmitted && (
            <button
              type="button"
              onClick={onReceiveTransfer}
              className="btn-success"
              disabled={receiveStatus === "saving"}
              title="Отметить как получено"
            >
              {receiveStatus === "saving" ? "Отмечаю…" : "Получено"}
            </button>
          )}
          {statusNorm !== "received" && statusNorm !== "cancelled" && (
            <button
              type="button"
              onClick={onCancelTransfer}
              className="btn-danger-large"
              disabled={cancelStatus === "saving"}
              title="Отменить перемещение"
            >
              {cancelStatus === "saving" ? "Отменяю…" : "Отменить"}
            </button>
          )}
          <button type="button" onClick={fetchTransfer} className="btn">
            Обновить
          </button>
          <button
            type="button"
            onClick={onExportExcel}
            className="btn"
            disabled={exportLoading !== null}
            title="Скачать Excel"
          >
            {exportLoading === "excel" ? "…" : "↓ Excel"}
          </button>
          <button
            type="button"
            onClick={handlePrintTransferPdf}
            className="btn"
            disabled={!transfer}
            title="Печать / сохранить как PDF"
          >
            🖨 PDF
          </button>
          {checkedCount > 0 && (
            <button
              type="button"
              onClick={onPrintChecked}
              className="btn-primary"
              title={`Печать ценников для ${checkedCount} товаров`}
            >
              🖨 Печать ценников ({checkedCount})
            </button>
          )}
        </div>
      </div>

      {loadStatus === "loading" && <Loading text="Загрузка…" />}
      {loadStatus === "failed" && <div className="error">{error}</div>}
      {submitStatus === "failed" && submitError && <div className="error">{submitError}</div>}
      {receiveStatus === "failed" && receiveError && <div className="error">{receiveError}</div>}
      {cancelStatus === "failed" && cancelError && <div className="error">{cancelError}</div>}
      {exportError && <div className="error">{exportError}</div>}

      {transfer && (
        <>
          <div className="meta">
            <div className="meta-row">
              <div className="meta-label">ID</div>
              <div className="meta-value-mono">{transfer.id ?? "—"}</div>
            </div>
            <div className="meta-row">
              <div className="meta-label">Название</div>
              <div className="meta-value">{transfer.name || "—"}</div>
            </div>
            <div className="meta-row">
              <div className="meta-label">Откуда</div>
              <div className="meta-value">{transfer.from_store_name || `Store #${transfer.from_store}` || "—"}</div>
            </div>
            <div className="meta-row">
              <div className="meta-label">Куда</div>
              <div className="meta-value">{transfer.to_store_name || `Store #${transfer.to_store}` || "—"}</div>
            </div>
            <div className="meta-row">
              <div className="meta-label">Статус</div>
              <div className="meta-value">{stockTransferStatusLabel(transfer.status)}</div>
            </div>
            <div className="meta-row">
              <div className="meta-label">Комментарий</div>
              <div className="meta-value">{transfer.comment || "—"}</div>
            </div>
            <div className="meta-row">
              <div className="meta-label">Итого по опт. цене</div>
              <div className="meta-value">{formatMoney(transfer.wholesale_total)}</div>
            </div>

            <div className="meta-row">
              <div className="meta-label">Создал</div>
              <div className="meta-value">{formatUser(transfer.created_by)}</div>
            </div>
            <div className="meta-row">
              <div className="meta-label">Отправил</div>
              <div className="meta-value">{formatUser(transfer.submitted_by)}</div>
            </div>
            <div className="meta-row">
              <div className="meta-label">Получил</div>
              <div className="meta-value">{formatUser(transfer.received_by)}</div>
            </div>

            <div className="meta-row">
              <div className="meta-label">Отправлено</div>
              <div className="meta-value">{formatDateTime(transfer.submitted_at)}</div>
            </div>
            <div className="meta-row">
              <div className="meta-label">Получено</div>
              <div className="meta-value">{formatDateTime(transfer.received_at)}</div>
            </div>
            <div className="meta-row">
              <div className="meta-label">Создано</div>
              <div className="meta-value">{formatDateTime(transfer.created_at)}</div>
            </div>
            <div className="meta-row">
              <div className="meta-label">Обновлено</div>
              <div className="meta-value">{formatDateTime(transfer.updated_at)}</div>
            </div>
          </div>

          <div className="body">
            <div className="section-title">Позиции: {items.length}</div>

            {isDraft && (
              <div className="add-card">
                <div className="add-title">Добавить товар</div>
                <div className="add-sub-title">
                  Штрихкод — сканер добавляет товар автоматически. Название или категория — выбери из списка ниже.
                  Поиск по штрихкоду идёт через <span className="mono">/api/product/products/check-inventory-store-by-barcode/</span>{" "}
                  (store_id берём из <span className="mono">from_store</span> этого перемещения).
                </div>

                <div className="add-row">
                  <div className="search-field-wrap">
                    <input
                      ref={barcodeInputRef}
                      value={barcode}
                      onChange={(e) => setBarcode(e.target.value)}
                      onFocus={() => {
                        if (!isNumericQuery && (searchResults.length > 0 || categoryResults.length > 0)) {
                          setSearchDropdownOpen(true);
                        }
                      }}
                      onBlur={() => {
                        window.setTimeout(() => setSearchDropdownOpen(false), 150);
                      }}
                      placeholder="Штрихкод, название или категория…"
                      className="big-input"
                      onKeyDown={(e) => {
                        if (e.key === "Escape") {
                          setSearchDropdownOpen(false);
                          return;
                        }
                        if (e.key !== "Enter") return;
                        e.preventDefault();
                        if (searchDropdownOpen && !isNumericQuery && searchResults.length > 0) {
                          addProductFromSearch(searchResults[0]);
                          return;
                        }
                        onScanAddByBarcode();
                      }}
                    />
                    {searchDropdownOpen && !isNumericQuery && (
                      <div className="search-dropdown">
                        {searchStatus === "loading" && <div className="search-dropdown-empty">Ищу…</div>}
                        {searchStatus !== "loading" && searchResults.length === 0 && categoryResults.length === 0 && (
                          <div className="search-dropdown-empty">Ничего не найдено</div>
                        )}
                        {searchResults.length > 0 && (
                          <div className="search-dropdown-section">
                            <div className="search-dropdown-section-title">Товары</div>
                            {searchResults.map((p) => (
                              <button
                                type="button"
                                key={p.id}
                                className="search-dropdown-item"
                                onMouseDown={(e) => e.preventDefault()}
                                onClick={() => addProductFromSearch(p)}
                              >
                                <span className="search-dropdown-name">{p.name}</span>
                                <span className="search-dropdown-meta">{p.barcode || "—"}</span>
                              </button>
                            ))}
                          </div>
                        )}
                        {!selectedCategoryId && categoryResults.length > 0 && (
                          <div className="search-dropdown-section">
                            <div className="search-dropdown-section-title">Категории</div>
                            {categoryResults.map((c) => (
                              <button
                                type="button"
                                key={c.id}
                                className="search-dropdown-item"
                                onMouseDown={(e) => e.preventDefault()}
                                onClick={() => {
                                  setSelectedCategoryId(c.id);
                                  dispatch(fetchProducts({ search: searchQueryTrimmed, categoryId: c.id, pageSize: 20 }));
                                  setSearchDropdownOpen(true);
                                  if (barcodeInputRef.current) barcodeInputRef.current.focus();
                                }}
                              >
                                <span className="search-dropdown-name">{c.name}</span>
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={onScanAddByBarcode}
                    className="btn-primary"
                    disabled={!barcodeNormalized || scanStatus === "loading" || scanStatus === "saving"}
                  >
                    {scanStatus === "saving" ? "Добавляю…" : scanStatus === "loading" ? "Поиск…" : "Добавить"}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setBarcode("");
                      setScanStatus("idle");
                      setScanError(null);
                      setSearchDropdownOpen(false);
                      setSelectedCategoryId(null);
                      if (barcodeInputRef.current) barcodeInputRef.current.focus();
                    }}
                    className="btn-ghost"
                  >
                    Очистить
                  </button>
                </div>

                {scanError && <div className="error-inline">{scanError}</div>}
              </div>
            )}

            <div className="table-wrap">
              <table className="table">
                <thead>
                  <tr>
                    <th className="th">ID</th>
                    <th className="th">Product</th>
                    <th className="th">Название</th>
                    <th className="th">Штрихкод</th>
                    <th className="th">На складе</th>
                    <th className="th">Количество</th>
                    <th className="th">Опт. цена</th>
                    <th className="th">Сумма</th>
                    <th className="th">Ценник</th>
                    <th className="th">Действия</th>
                  </tr>
                </thead>
                <tbody>
                  {items.length === 0 ? (
                    <tr>
                      <td colSpan={10} className="td-muted">
                        Нет позиций
                      </td>
                    </tr>
                  ) : (
                    itemsSorted.map((it) => {
                      const stock = it.stock_at_from_store ?? null;
                      const need = Number(it.quantity) || 0;
                      const insufficient = isDraft && stock != null && stock < need;
                      return (
                      <tr key={it.id} className={insufficient ? "tr-stock-warn" : undefined}>
                        <td className="td-mono">{it.id}</td>
                        <td className="td-mono">{it.product ?? "—"}</td>
                        <td className="td">{it.product_name || "—"}</td>
                        <td className="td-mono">{it.product_barcode || "—"}</td>
                        <td className="td-mono">
                          {stock == null ? "—" : stock}
                          {insufficient && (
                            <div className="stock-warn-msg">
                              {stock <= 0 ? "Нет на складе этого магазина" : `Недостаточно: доступно ${stock}, нужно ${need}`}
                            </div>
                          )}
                        </td>
                        <td
                          className="td"
                          onDoubleClick={() => {
                            if (!isDraft) return;
                            setEditingItemId(String(it.id));
                            window.setTimeout(() => {
                              const el = qtyInputRefByItemId.current[String(it.id)];
                              if (el && typeof el.focus === "function") {
                                el.focus();
                                if (typeof el.select === "function") el.select();
                              }
                            }, 0);
                          }}
                          title={isDraft ? "Двойной клик — редактировать количество" : undefined}
                        >
                          {isDraft && String(editingItemId) === String(it.id) ? (
                            <input
                              ref={(el) => {
                                if (el) qtyInputRefByItemId.current[String(it.id)] = el;
                              }}
                              value={qtyDraftById[String(it.id)] ?? ""}
                              onChange={(e) => {
                                const v = e.target.value;
                                setQtyDraftById((p) => ({ ...p, [String(it.id)]: v }));
                                setSaveErrorById((p) => ({ ...p, [String(it.id)]: null }));
                              }}
                              onKeyDown={(e) => {
                                if (e.key === "Enter") onSaveItemQty(it.id);
                                if (e.key === "Escape") setEditingItemId(null);
                              }}
                              className="qty-input-cell"
                              inputMode="numeric"
                              type="number"
                              step="1"
                              min="1"
                            />
                          ) : (
                            <span className={isDraft ? "qty-readonly-hint" : undefined}>{it.quantity ?? "—"}</span>
                          )}
                        </td>
                        <td className="td">{formatMoney(it.wholesale_price)}</td>
                        <td className="td">{formatMoney(it.line_wholesale_total)}</td>
                        <td className="td" style={{ textAlign: "center" }}>
                          <label style={{ cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 4 }}>
                            <input
                              type="checkbox"
                              checked={!!printCheckedById[String(it.id)]}
                              onChange={(e) => togglePrintCheck(it.id, e.target.checked)}
                              style={{ width: 16, height: 16, cursor: "pointer" }}
                            />
                          </label>
                        </td>
                        <td className="td">
                          {isDraft && String(editingItemId) === String(it.id) && (
                            <button
                              type="button"
                              onClick={() => onSaveItemQty(it.id)}
                              className="btn-small-primary"
                              disabled={!isDraft || saveStatusById[String(it.id)] === "saving"}
                              title="Сохранить количество"
                            >
                              {saveStatusById[String(it.id)] === "saving" ? "Сохраняю…" : "Сохранить"}
                            </button>
                          )}
                          {isDraft && (
                            <>
                              <button
                                type="button"
                                onClick={() => onDeleteItem(it.id)}
                                className="btn-danger"
                                disabled={deleteStatusById[String(it.id)] === "saving"}
                                title="Удалить позицию"
                              >
                                {deleteStatusById[String(it.id)] === "saving" ? "Удаляю…" : "Удалить"}
                              </button>
                              {deleteErrorById[String(it.id)] && <div className="error-mini">{deleteErrorById[String(it.id)]}</div>}
                            </>
                          )}
                          {isDraft && saveErrorById[String(it.id)] && <div className="error-mini">{saveErrorById[String(it.id)]}</div>}
                        </td>
                      </tr>
                      );
                    })
                  )}
                </tbody>
                {items.length > 0 && (
                  <tfoot>
                    <tr>
                      <td className="td-mono" colSpan={7} style={{ textAlign: "right", fontWeight: 600 }}>
                        Итого по опт. цене
                      </td>
                      <td className="td" style={{ fontWeight: 600 }}>{formatMoney(transfer.wholesale_total)}</td>
                      <td className="td" colSpan={2} />
                    </tr>
                  </tfoot>
                )}
              </table>
            </div>
          </div>
        </>
      )}

      {printNowLabels !== null && createPortal(
        <div id="transfer-print-root" style={{ position: "fixed", left: -10000, top: 0 }}>
          <style>{labelPrintCss}</style>
          {printNowLabels.map((label, idx) => (
            // eslint-disable-next-line react/no-array-index-key
            <div key={idx} className="transfer-print-label" style={labelWrapStyle}>
              <div style={labelCompanyStyle}>AUF</div>
              <div style={labelNameStyle} title={label.name}>{label.name}</div>
              <div style={labelBarcodeStyle}>
                {label.barcode ? (
                  <Barcode
                    value={label.barcode}
                    height={30}
                    width={1.0}
                    margin={0}
                    displayValue
                    background="#ffffff"
                    lineColor="#000000"
                    textAlign="center"
                    fontSize={11}
                  />
                ) : (
                  <span style={{ opacity: 0.5 }}>—</span>
                )}
              </div>
              <div style={labelPriceStyle}>
                Цена&nbsp;{label.price != null ? label.price.toFixed(2) : "—"}
              </div>
            </div>
          ))}
        </div>,
        document.body
      )}
    </div>
  );
}

